import React, { useState } from 'react';
import {
  Card,
  CardContent,
  CardActions,
  Typography,
  IconButton,
  Chip,
  Box,
  Tooltip,
  Checkbox,
  Badge,
} from '@mui/material';
import { QrCode2, Download, Delete, ContentCopy, Folder as FolderIcon, GetApp } from '@mui/icons-material';
import FileIcon from '../common/FileIcon';
import { formatFileSize, formatDateShort } from '../../utils/formatters';

export default function FileCard({
  file,
  onQRClick,
  onDelete,
  onDownload,
  onCopyLink,
  onContextMenu,
  onFileClick,
  search,
  selectionMode,
  selected,
  onToggleSelect,
}) {
  const [hovered, setHovered] = useState(false);

  const handleContextMenu = (e) => {
    e.preventDefault();
    if (onContextMenu) onContextMenu(e, file);
  };

  const handleDragStart = (e) => {
    e.dataTransfer.setData('application/x-file-id', file.id);
    e.dataTransfer.effectAllowed = 'move';
  };

  const handleCheckboxClick = (e) => {
    e.stopPropagation();
    if (onToggleSelect) onToggleSelect(file.id);
  };

  const handleCardClick = (e) => {
    // If in selection mode, toggle selection
    if (selectionMode) {
      if (onToggleSelect) onToggleSelect(file.id);
      return;
    }
    // Only trigger preview if clicking on the card itself, not on action buttons
    if (e.target.closest('button')) return;
    if (onFileClick) onFileClick(file);
  };

  const showCheckbox = selectionMode || hovered || selected;

  return (
    <Card
      sx={{
        display: 'flex',
        flexDirection: 'column',
        height: '100%',
        cursor: selectionMode ? 'pointer' : 'pointer',
        position: 'relative',
        outline: selected ? '2px solid' : 'none',
        outlineColor: 'primary.main',
        transition: 'outline 0.1s',
      }}
      onContextMenu={handleContextMenu}
      draggable={!selectionMode}
      onDragStart={!selectionMode ? handleDragStart : undefined}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      onClick={handleCardClick}
    >
      {/* Checkbox overlay */}
      {showCheckbox && (
        <Box
          sx={{
            position: 'absolute',
            top: 6,
            right: 6,
            zIndex: 1,
            bgcolor: 'background.paper',
            borderRadius: '50%',
            lineHeight: 0,
          }}
          onClick={handleCheckboxClick}
        >
          <Checkbox
            checked={!!selected}
            size="small"
            sx={{ p: 0.25 }}
          />
        </Box>
      )}

      <CardContent sx={{ flex: 1, pb: 1 }}>
        <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1.5, mb: 1.5 }}>
          <FileIcon mimeType={file.mime_type} size="large" />
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Tooltip title={file.original_filename} placement="top-start">
              <Typography variant="subtitle1" noWrap>
                {file.original_filename}
              </Typography>
            </Tooltip>
            <Typography variant="caption" color="text.secondary">
              {formatDateShort(file.created_at)}
            </Typography>
          </Box>
        </Box>

        <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
          <Chip label={formatFileSize(file.file_size)} size="small" variant="outlined" />
          <Chip
            label={`${file.download_count} download`}
            size="small"
            color="primary"
            variant="outlined"
          />
        </Box>

        {search && (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mt: 1 }}>
            <FolderIcon sx={{ fontSize: 14, color: 'text.secondary' }} />
            <Typography variant="caption" color="text.secondary" noWrap>
              {file.folder_name || 'Root'}
            </Typography>
          </Box>
        )}
      </CardContent>

      <CardActions sx={{ justifyContent: 'space-between', px: 2, pb: 1.5 }}>
        <Box>
          <Tooltip title={file.qr_downloaded ? "Scarica QR Code" : "Genera e scarica QR Code"}>
            <IconButton
              size="small"
              onClick={(e) => { e.stopPropagation(); onQRClick(file); }}
              sx={file.qr_downloaded ? {
                bgcolor: 'success.main',
                color: 'white',
                '&:hover': { bgcolor: 'success.dark' },
                width: 30,
                height: 30,
              } : { color: 'text.secondary' }}
            >
              {file.qr_downloaded ? (
                <Badge
                  badgeContent={<GetApp sx={{ fontSize: 10 }} />}
                  sx={{ '& .MuiBadge-badge': { bgcolor: 'white', color: 'success.main', minWidth: 14, height: 14, p: 0 } }}
                >
                  <QrCode2 fontSize="small" />
                </Badge>
              ) : (
                <QrCode2 />
              )}
            </IconButton>
          </Tooltip>
          <Tooltip title="Copia link">
            <IconButton size="small" onClick={(e) => { e.stopPropagation(); onCopyLink(file); }}>
              <ContentCopy fontSize="small" />
            </IconButton>
          </Tooltip>
        </Box>
        <Box>
          <Tooltip title="Download">
            <IconButton size="small" onClick={(e) => { e.stopPropagation(); onDownload(file); }}>
              <Download fontSize="small" />
            </IconButton>
          </Tooltip>
          <Tooltip title={file.qr_downloaded ? "Eliminazione bloccata (QR scaricato)" : "Elimina"}>
            <span>
            <IconButton size="small" color="error" disabled={file.qr_downloaded} onClick={(e) => { e.stopPropagation(); onDelete(file); }}>
              <Delete fontSize="small" />
            </IconButton>
            </span>
          </Tooltip>
        </Box>
      </CardActions>
    </Card>
  );
}
